// /app/frontend/src/components/admin/ImageGalleryModal.jsx
// Centralized Media Gallery for CMS - browse, select, and manage images & videos with tags

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Image, Upload, Search, Check, Trash2, Loader2, X, FolderOpen,
  Grid, List, RefreshCw, Package, Layers, Star, Settings, ImageIcon,
  Video, Play, Tag, Plus, Maximize2, ChevronLeft, ChevronRight, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category tabs configuration
const CATEGORY_TABS = [
  { id: 'all', label: 'All', icon: Grid },
  { id: 'general', label: 'General', icon: FolderOpen },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'categories', label: 'Categories', icon: Layers },
  { id: 'features', label: 'Features', icon: Star },
  { id: 'systems', label: 'Systems', icon: Settings },
  { id: 'benefits', label: 'Benefits', icon: Check },
];

// Media type tabs
const MEDIA_TABS = [
  { id: 'all', label: 'All Media', icon: Grid },
  { id: 'image', label: 'Images', icon: ImageIcon },
  { id: 'video', label: 'Videos', icon: Video },
];

const ImageGalleryModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  token, 
  multiple = false, 
  defaultCategory = 'general',
  acceptVideo = true 
}) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [dragActive, setDragActive] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeMediaType, setActiveMediaType] = useState('all');
  const [uploadCategory, setUploadCategory] = useState(defaultCategory);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [typeCounts, setTypeCounts] = useState({ image: 0, video: 0, all: 0 });
  
  // Full-size preview state
  const [previewItem, setPreviewItem] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(-1);
  
  // Tagging state
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [editingTags, setEditingTags] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [uploadTags, setUploadTags] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
      fetchCategoryCounts();
      fetchTags();
      setSelectedMedia([]);
      setUploadCategory(defaultCategory);
      setPreviewItem(null);
    }
  }, [isOpen, defaultCategory]);

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [activeCategory, activeMediaType, selectedTag]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/cms/media?`;
      if (activeCategory !== 'all') url += `category=${activeCategory}&`;
      if (activeMediaType !== 'all') url += `media_type=${activeMediaType}&`;
      if (selectedTag) url += `tag=${selectedTag}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media');
    }
    setLoading(false);
  };

  const fetchCategoryCounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/cms/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const catCounts = {};
      const typeCounts = { image: 0, video: 0, all: 0 };
      
      response.data.forEach(item => {
        const cat = item.category || 'general';
        catCounts[cat] = (catCounts[cat] || 0) + 1;
        
        const type = item.media_type || 'image';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
        typeCounts.all++;
      });
      catCounts.all = response.data.length;
      setCategoryCounts(catCounts);
      setTypeCounts(typeCounts);
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/cms/media/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploaded = [];
    
    for (const file of Array.from(files)) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      
      if (!isVideo && !isImage) {
        toast.error(`${file.name} is not a valid image or video`);
        continue;
      }
      
      if (!acceptVideo && isVideo) {
        toast.error(`Videos not accepted in this context`);
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const tagsParam = uploadTags.length > 0 ? `&tags=${uploadTags.join(',')}` : '';
        const response = await axios.post(
          `${API_URL}/api/admin/cms/upload-media?category=${uploadCategory}${tagsParam}`, 
          formData, 
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        uploaded.push(response.data.media);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploaded.length > 0) {
      setMedia(prev => [...uploaded, ...prev]);
      fetchCategoryCounts();
      fetchTags();
      const mediaType = uploaded.some(m => m.media_type === 'video') ? 'media' : 'image';
      toast.success(`${uploaded.length} ${mediaType}${uploaded.length > 1 ? 's' : ''} uploaded`);
    }
    setUploading(false);
  };

  const handleDelete = async (mediaId, mediaUrl) => {
    if (!window.confirm('Delete this media? It may still be used in some content.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/cms/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(prev => prev.filter(m => m.id !== mediaId));
      setSelectedMedia(prev => prev.filter(url => url !== mediaUrl));
      fetchCategoryCounts();
      toast.success('Media deleted');
    } catch (error) {
      setMedia(prev => prev.filter(m => m.id !== mediaId));
      toast.success('Media removed from gallery');
    }
  };

  const handleUpdateTags = async (mediaId, tags) => {
    try {
      await axios.put(`${API_URL}/api/admin/cms/media/${mediaId}/tags`, tags, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setMedia(prev => prev.map(m => m.id === mediaId ? { ...m, tags } : m));
      fetchTags();
      toast.success('Tags updated');
      setEditingTags(null);
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  const [autoTagging, setAutoTagging] = useState(null); // media ID being auto-tagged

  const handleAutoTag = async (mediaItem) => {
    if (mediaItem.media_type === 'video') {
      toast.error('Auto-tagging is only available for images');
      return;
    }
    
    setAutoTagging(mediaItem.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/cms/media/${mediaItem.id}/auto-tag`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the media item with new tags
      setMedia(prev => prev.map(m => 
        m.id === mediaItem.id ? { ...m, tags: response.data.all_tags, ai_tagged: true } : m
      ));
      
      // Update editing tags if open
      if (editingTags?.id === mediaItem.id) {
        setEditingTags({ ...editingTags, tags: response.data.all_tags });
      }
      
      fetchTags();
      toast.success(`AI suggested ${response.data.suggested_tags.length} tags: ${response.data.suggested_tags.join(', ')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-tagging failed');
    } finally {
      setAutoTagging(null);
    }
  };

  const toggleSelect = (mediaUrl) => {
    if (multiple) {
      setSelectedMedia(prev => 
        prev.includes(mediaUrl) 
          ? prev.filter(url => url !== mediaUrl)
          : [...prev, mediaUrl]
      );
    } else {
      setSelectedMedia([mediaUrl]);
    }
  };

  const handleConfirm = () => {
    if (selectedMedia.length > 0) {
      onSelect(multiple ? selectedMedia : selectedMedia[0]);
      onClose();
    }
  };

  const handleSeedExisting = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/cms/media/seed-existing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchMedia();
      fetchCategoryCounts();
    } catch (error) {
      toast.error('Failed to seed media');
    }
  };

  // Full-size preview navigation
  const openPreview = (item, index) => {
    setPreviewItem(item);
    setPreviewIndex(index);
  };

  const navigatePreview = (direction) => {
    const newIndex = previewIndex + direction;
    if (newIndex >= 0 && newIndex < filteredMedia.length) {
      setPreviewItem(filteredMedia[newIndex]);
      setPreviewIndex(newIndex);
    }
  };

  // Keyboard navigation for preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewItem) return;
      if (e.key === 'Escape') setPreviewItem(null);
      if (e.key === 'ArrowLeft') navigatePreview(-1);
      if (e.key === 'ArrowRight') navigatePreview(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewItem, previewIndex]);

  const filteredMedia = media.filter(item => 
    !searchQuery || 
    item.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.original_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getMediaUrl = (url) => url?.startsWith('/') ? `${API_URL}${url}` : url;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Media Gallery
            <span className="text-sm font-normal text-neutral-500 ml-2">
              {typeCounts.image} images, {typeCounts.video} videos
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Media Type Tabs */}
        <div className="flex items-center gap-2 pb-2">
          {MEDIA_TABS.map(tab => {
            const TabIcon = tab.icon;
            const count = typeCounts[tab.id] || 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveMediaType(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeMediaType === tab.id
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeMediaType === tab.id ? 'bg-purple-200' : 'bg-neutral-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1 border-b pb-2 overflow-x-auto">
          {CATEGORY_TABS.map(tab => {
            const TabIcon = tab.icon;
            const count = categoryCounts[tab.id] || 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveCategory(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === tab.id
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeCategory === tab.id ? 'bg-amber-200' : 'bg-neutral-200'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSeedExisting}
            className="ml-auto text-xs"
            title="Import existing media from products and categories"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Import Existing
          </Button>
        </div>

        {/* Tags Filter */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 py-1 flex-wrap">
            <Tag className="w-4 h-4 text-neutral-400" />
            <button
              onClick={() => setSelectedTag('')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                !selectedTag ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 hover:bg-neutral-200'
              }`}
            >
              All Tags
            </button>
            {allTags.slice(0, 15).map(t => (
              <button
                key={t.tag}
                onClick={() => setSelectedTag(selectedTag === t.tag ? '' : t.tag)}
                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                  selectedTag === t.tag ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 hover:bg-neutral-200'
                }`}
              >
                {t.tag} ({t.count})
              </button>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-3 py-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by name or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          {/* View Toggle */}
          <div className="flex border rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-neutral-100' : ''}`}
              title="Grid view"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-neutral-100' : ''}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Upload Category Selector */}
          <select
            value={uploadCategory}
            onChange={(e) => setUploadCategory(e.target.value)}
            className="h-9 px-2 border rounded-md text-sm bg-white"
          >
            <option value="general">Upload to: General</option>
            <option value="products">Upload to: Products</option>
            <option value="categories">Upload to: Categories</option>
            <option value="features">Upload to: Features</option>
            <option value="systems">Upload to: Systems</option>
            <option value="benefits">Upload to: Benefits</option>
          </select>

          {/* Upload Tags */}
          <div className="flex items-center gap-1">
            <Input
              placeholder="Add tags (comma separated)"
              value={uploadTags.join(', ')}
              onChange={(e) => setUploadTags(e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              className="h-9 w-40 text-sm"
            />
          </div>

          {/* Refresh */}
          <Button variant="outline" size="sm" onClick={fetchMedia} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          {/* Upload */}
          <label className={`inline-flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-md cursor-pointer hover:bg-amber-700 transition-colors ${uploading ? 'opacity-50' : ''}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="text-sm font-medium">Upload</span>
            <input
              type="file"
              accept={acceptVideo ? "image/*,video/*" : "image/*"}
              multiple
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              disabled={uploading}
            />
          </label>
        </div>

        {/* Media Grid */}
        <div
          className={`flex-1 overflow-auto p-4 rounded-lg border ${dragActive ? 'bg-amber-50 ring-2 ring-amber-500 ring-inset' : 'bg-neutral-50'}`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            handleUpload(e.dataTransfer.files);
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-neutral-500">
              <ImageIcon className="w-12 h-12 mb-3 text-neutral-300" />
              <p className="text-lg font-medium">No media found</p>
              <p className="text-sm">Drag & drop images or videos here, or click Upload</p>
              {activeCategory === 'all' && activeMediaType === 'all' && !selectedTag && (
                <Button variant="outline" size="sm" className="mt-3" onClick={handleSeedExisting}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Import Existing Media
                </Button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-6 gap-3">
              {filteredMedia.map((item, index) => (
                <MediaGridItem
                  key={item.id || item.url}
                  item={item}
                  index={index}
                  isSelected={selectedMedia.includes(item.url)}
                  onSelect={() => toggleSelect(item.url)}
                  onDelete={() => handleDelete(item.id, item.url)}
                  onPreview={() => openPreview(item, index)}
                  onEditTags={() => setEditingTags(item)}
                  onAutoTag={() => handleAutoTag(item)}
                  isAutoTagging={autoTagging === item.id}
                  getMediaUrl={getMediaUrl}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMedia.map((item, index) => (
                <MediaListItem
                  key={item.id || item.url}
                  item={item}
                  index={index}
                  isSelected={selectedMedia.includes(item.url)}
                  onSelect={() => toggleSelect(item.url)}
                  onDelete={() => handleDelete(item.id, item.url)}
                  onPreview={() => openPreview(item, index)}
                  onEditTags={() => setEditingTags(item)}
                  onAutoTag={() => handleAutoTag(item)}
                  isAutoTagging={autoTagging === item.id}
                  getMediaUrl={getMediaUrl}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-neutral-500">
            {selectedMedia.length > 0 
              ? `${selectedMedia.length} item${selectedMedia.length > 1 ? 's' : ''} selected`
              : 'Click to select, double-click to preview full size'
            }
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleConfirm}
              disabled={selectedMedia.length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {multiple ? 'Add Selected' : 'Select'}
            </Button>
          </div>
        </div>

        {/* Full-size Preview Modal */}
        {previewItem && (
          <div 
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
            onClick={() => setPreviewItem(null)}
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
              onClick={() => setPreviewItem(null)}
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation */}
            {previewIndex > 0 && (
              <button 
                className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                onClick={(e) => { e.stopPropagation(); navigatePreview(-1); }}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}
            {previewIndex < filteredMedia.length - 1 && (
              <button 
                className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                onClick={(e) => { e.stopPropagation(); navigatePreview(1); }}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            )}
            
            {/* Media content */}
            <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              {previewItem.media_type === 'video' ? (
                <video
                  src={getMediaUrl(previewItem.url)}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh] rounded-lg"
                />
              ) : (
                <img
                  src={getMediaUrl(previewItem.url)}
                  alt={previewItem.original_name || previewItem.filename}
                  className="max-w-full max-h-[85vh] object-contain rounded-lg"
                />
              )}
              
              {/* Info bar */}
              <div className="mt-4 text-white text-center">
                <p className="font-medium">{previewItem.original_name || previewItem.filename}</p>
                <div className="flex items-center justify-center gap-2 mt-1 text-sm text-white/70">
                  <span className="bg-white/20 px-2 py-0.5 rounded">{previewItem.category}</span>
                  {previewItem.tags?.map(tag => (
                    <span key={tag} className="bg-blue-500/50 px-2 py-0.5 rounded">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Tags Modal */}
        {editingTags && (
          <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center" onClick={() => setEditingTags(null)}>
            <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5" /> Edit Tags
              </h3>
              
              {/* Current tags */}
              <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] p-2 border rounded-md bg-neutral-50">
                {(editingTags.tags || []).map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
                    {tag}
                    <button 
                      onClick={() => setEditingTags({
                        ...editingTags,
                        tags: editingTags.tags.filter(t => t !== tag)
                      })}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {(!editingTags.tags || editingTags.tags.length === 0) && (
                  <span className="text-neutral-400 text-sm">No tags</span>
                )}
              </div>
              
              {/* Add new tag */}
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Add tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      const tags = [...(editingTags.tags || []), newTag.trim()];
                      setEditingTags({ ...editingTags, tags });
                      setNewTag('');
                    }
                  }}
                />
                <Button 
                  variant="outline"
                  onClick={() => {
                    if (newTag.trim()) {
                      const tags = [...(editingTags.tags || []), newTag.trim()];
                      setEditingTags({ ...editingTags, tags });
                      setNewTag('');
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* AI Auto-tag button */}
              {editingTags.media_type !== 'video' && (
                <div className="mb-4">
                  <Button 
                    variant="outline" 
                    className="w-full border-purple-300 text-purple-600 hover:bg-purple-50"
                    onClick={() => handleAutoTag(editingTags)}
                    disabled={autoTagging === editingTags.id}
                  >
                    {autoTagging === editingTags.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing image...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Auto-Tag with AI</>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Suggested tags */}
              {allTags.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-neutral-500 mb-2">Suggested tags:</p>
                  <div className="flex flex-wrap gap-1">
                    {allTags.slice(0, 10).filter(t => !editingTags.tags?.includes(t.tag)).map(t => (
                      <button
                        key={t.tag}
                        onClick={() => setEditingTags({
                          ...editingTags,
                          tags: [...(editingTags.tags || []), t.tag]
                        })}
                        className="text-xs px-2 py-1 bg-neutral-100 hover:bg-neutral-200 rounded"
                      >
                        + {t.tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingTags(null)}>Cancel</Button>
                <Button onClick={() => handleUpdateTags(editingTags.id, editingTags.tags || [])}>
                  Save Tags
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Grid item component
const MediaGridItem = ({ item, index, isSelected, onSelect, onDelete, onPreview, onEditTags, onAutoTag, isAutoTagging, getMediaUrl }) => {
  const isVideo = item.media_type === 'video';
  
  return (
    <div
      onClick={onSelect}
      onDoubleClick={onPreview}
      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
        isSelected 
          ? 'border-amber-500 ring-2 ring-amber-200' 
          : 'border-transparent hover:border-neutral-300'
      }`}
    >
      {isVideo ? (
        <div className="relative w-full h-28 bg-neutral-900">
          <video
            src={getMediaUrl(item.url)}
            className="w-full h-full object-cover"
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="w-5 h-5 text-neutral-700 ml-0.5" />
            </div>
          </div>
        </div>
      ) : (
        <img
          src={getMediaUrl(item.url)}
          alt={item.original_name || item.filename}
          className="w-full h-28 object-cover"
        />
      )}
      
      {/* Type badge */}
      {isVideo && (
        <div className="absolute top-1 left-1 text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded flex items-center gap-1">
          <Video className="w-3 h-3" /> Video
        </div>
      )}
      
      {/* Category badge */}
      {item.category && item.category !== 'general' && !isVideo && (
        <div className="absolute top-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">
          {item.category}
        </div>
      )}
      
      {/* Tags indicator */}
      {item.tags?.length > 0 && (
        <div className="absolute top-1 right-8 text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
          <Tag className="w-3 h-3" /> {item.tags.length}
        </div>
      )}
      
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-1 right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
        <p className="text-white text-xs truncate mb-1">{item.filename || item.original_name || 'Media'}</p>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="p-1 bg-white/80 rounded text-neutral-700 hover:bg-white"
            title="View full size"
            data-testid="media-preview-btn"
          >
            <Maximize2 className="w-3 h-3" />
          </button>
          {!isVideo && (
            <button
              onClick={(e) => { e.stopPropagation(); onAutoTag(); }}
              className={`p-1 rounded text-white ${isAutoTagging ? 'bg-purple-400' : 'bg-purple-500 hover:bg-purple-600'}`}
              title="Auto-tag with AI"
              disabled={isAutoTagging}
              data-testid="media-autotag-btn"
            >
              {isAutoTagging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onEditTags(); }}
            className="p-1 bg-blue-500 rounded text-white hover:bg-blue-600"
            title="Edit tags"
            data-testid="media-edittags-btn"
          >
            <Tag className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 bg-red-500 rounded text-white hover:bg-red-600"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

// List item component
const MediaListItem = ({ item, index, isSelected, onSelect, onDelete, onPreview, onEditTags, onAutoTag, isAutoTagging, getMediaUrl }) => {
  const isVideo = item.media_type === 'video';
  
  return (
    <div
      onClick={onSelect}
      onDoubleClick={onPreview}
      className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-all bg-white ${
        isSelected 
          ? 'border-amber-500 bg-amber-50' 
          : 'border-neutral-200 hover:border-neutral-300'
      }`}
    >
      <div className="relative w-16 h-16 flex-shrink-0 rounded overflow-hidden">
        {isVideo ? (
          <div className="w-full h-full bg-neutral-900 flex items-center justify-center">
            <video src={getMediaUrl(item.url)} className="w-full h-full object-cover" muted />
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-6 h-6 text-white" />
            </div>
          </div>
        ) : (
          <img
            src={getMediaUrl(item.url)}
            alt={item.original_name || item.filename}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{item.filename || item.original_name || 'Media'}</p>
        <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1 flex-wrap">
          <span className={`px-1.5 py-0.5 rounded ${isVideo ? 'bg-purple-100 text-purple-700' : 'bg-neutral-100'}`}>
            {isVideo ? 'Video' : 'Image'}
          </span>
          {item.category && (
            <span className="bg-neutral-100 px-1.5 py-0.5 rounded">{item.category}</span>
          )}
          {item.tags?.map(tag => (
            <span key={tag} className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{tag}</span>
          ))}
          {item.uploaded_at && (
            <span>{new Date(item.uploaded_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      
      {isSelected && <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />}
      
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="p-2 text-neutral-500 hover:bg-neutral-100 rounded"
          title="View full size"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        {!isVideo && (
          <button
            onClick={(e) => { e.stopPropagation(); onAutoTag(); }}
            className={`p-2 rounded ${isAutoTagging ? 'text-purple-400' : 'text-purple-500 hover:bg-purple-50'}`}
            title="Auto-tag with AI"
            disabled={isAutoTagging}
          >
            {isAutoTagging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onEditTags(); }}
          className="p-2 text-blue-500 hover:bg-blue-50 rounded"
          title="Edit tags"
        >
          <Tag className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-2 text-red-500 hover:bg-red-50 rounded"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ImageGalleryModal;
